import { useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { 
  ChevronLeft, ChevronDown, Image, Video, X, Upload, Check,
  Heart, Frown, Sunrise, Sparkles, Users, MessageCircle, Coffee, Briefcase, HeartPulse,
  Globe, MapPin, UserPlus, Trophy, Megaphone, Flame, Hand, HelpCircle,
  Loader2, ShieldCheck, Search, Send, Mail,
  Plane, Activity, Palette, Wallet, Camera, Lock, MessageSquare, Home
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { CATEGORIES } from "../data/communityCategories";

// 커뮤니티 신규 카테고리(communityCategories.js)와 동일하게 구성.
// 각 대카테고리(선택 가능) + 서브카테고리(선택 가능)를 평면 리스트로 펼침.
const NEW_ICON_MAP = {
  Home, Heart, Plane, Activity, Palette, Wallet, Coffee, Camera, Lock, MessageSquare, UserPlus,
};

const categories = CATEGORIES
  .filter((c) => c.isFeed && c.id !== "home" && !c.href)
  .flatMap((c) => {
    const Icon = NEW_ICON_MAP[c.icon] || MessageCircle;
    const main = { id: c.id, title: c.label, icon: Icon, color: "text-burgundy-600" };
    const subs = (c.sub || []).map((s) => ({
      id: s.id,
      title: `${c.label} · ${s.label}`,
      icon: Icon,
      color: "text-stone-400",
      isSub: true,
    }));
    return [main, ...subs];
  });

export const WritePostPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "";
  const prefillContent = searchParams.get("prefill") || "";
  
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(prefillContent);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiMatching, setAiMatching] = useState(false);
  const [aiMatches, setAiMatches] = useState(null);
  const [activeMessageUserId, setActiveMessageUserId] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [messageSending, setMessageSending] = useState(false);
  const [messageSentIds, setMessageSentIds] = useState([]);
  
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const selectedCategoryInfo = categories.find(c => c.id === selectedCategory);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 10)); // Max 10 images
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    const accepted = [];
    for (const file of files) {
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > 50) {
        alert(`"${file.name}" 파일이 ${sizeMB.toFixed(1)}MB로 업로드 한도(50MB)를 초과합니다.\n압축하거나 짧게 잘라 다시 시도해주세요.`);
        continue;
      }
      accepted.push({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
      });
    }
    setVideos(prev => [...prev, ...accepted].slice(0, 3));
    // reset so same file can be re-selected
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedCategory || !title.trim() || !content.trim()) {
      alert("카테고리, 제목, 내용을 모두 입력해주세요.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Upload images first
      const uploadedImages = [];
      for (const image of images) {
        const formData = new FormData();
        formData.append("file", image.file);
        formData.append("file_type", "image");
        
        const response = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          body: formData,
        });
        
        if (response.ok) {
          const data = await response.json();
          uploadedImages.push(data.url);
        }
      }
      
      // Upload videos
      const uploadedVideos = [];
      for (const video of videos) {
        // Pre-flight size check (50MB hard limit due to ingress)
        const sizeMB = video.file.size / (1024 * 1024);
        if (sizeMB > 50) {
          alert(`영상 "${video.name}" 파일이 너무 큽니다 (${sizeMB.toFixed(1)}MB).\n현재 서버 업로드 한도는 50MB입니다.\n파일을 압축하거나 짧게 잘라 다시 시도해주세요.`);
          setIsSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append("file", video.file);
        formData.append("file_type", "video");
        
        try {
          const response = await fetch(`${API_URL}/api/upload`, {
            method: "POST",
            body: formData,
          });
          
          if (response.ok) {
            const data = await response.json();
            uploadedVideos.push(data.url);
          } else {
            let errMsg = "";
            try { const e = await response.json(); errMsg = e.detail || JSON.stringify(e); } catch { errMsg = `HTTP ${response.status}`; }
            alert(`영상 "${video.name}" 업로드 실패: ${errMsg}\n(파일 크기: ${sizeMB.toFixed(1)}MB)`);
            setIsSubmitting(false);
            return;
          }
        } catch (uploadErr) {
          alert(`영상 "${video.name}" 업로드 중 네트워크 오류가 발생했습니다.\n파일이 너무 크거나(50MB 초과) 네트워크가 불안정할 수 있습니다.`);
          setIsSubmitting(false);
          return;
        }
      }
      
      // Create post
      const postData = {
        category: selectedCategory,
        topic: selectedCategory,
        title: title.trim(),
        content: content.trim(),
        images: uploadedImages,
        videos: uploadedVideos,
      };
      
      // Include auth token (token from useAuth or fallback to localStorage)
      const bearer = token || localStorage.getItem("liztv_token");
      if (!bearer) {
        alert("로그인이 만료되었습니다. 다시 로그인 후 작성해주세요.");
        setIsSubmitting(false);
        return;
      }
      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${bearer}`,
      };
      
      const response = await fetch(`${API_URL}/api/posts`, {
        method: "POST",
        headers,
        body: JSON.stringify(postData),
      });
      
      if (response.ok) {
        // AI 매칭 시작
        setAiMatching(true);
        try {
          const matchRes = await fetch(`${API_URL}/api/ai-match/recommend`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ title: title.trim(), content: content.trim() }),
          });
          if (matchRes.ok) {
            const matchData = await matchRes.json();
            if (matchData.matches && matchData.matches.length > 0) {
              setAiMatches(matchData.matches);
            } else {
              setAiMatches([]);
            }
          } else {
            setAiMatches([]);
          }
        } catch {
          setAiMatches([]);
        } finally {
          setAiMatching(false);
        }
      } else {
        const error = await response.json();
        alert(`등록 실패: ${error.detail || "알 수 없는 오류"}`);
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("글 등록 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendMessage = async (receiverId) => {
    if (!messageText.trim()) return;
    setMessageSending(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ receiver_id: receiverId, content: messageText.trim() }),
      });
      if (res.ok) {
        setMessageSentIds(prev => [...prev, receiverId]);
        setActiveMessageUserId(null);
        setMessageText("");
      } else {
        const err = await res.json();
        alert(err.detail || "메시지 전송에 실패했습니다.");
      }
    } catch {
      alert("메시지 전송 중 오류가 발생했습니다.");
    } finally {
      setMessageSending(false);
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#FAFAF9] pt-20"}>
      {/* AI 매칭 로딩 팝업 */}
      {aiMatching && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="ai-matching-loading">
          <div className="bg-white rounded-2xl p-10 max-w-sm mx-4 text-center shadow-2xl">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-stone-200" />
              <div className="absolute inset-0 rounded-full border-4 border-t-burgundy-500 animate-spin" />
              <Search className="absolute inset-0 m-auto w-8 h-8 text-burgundy-400" />
            </div>
            <h3 className="text-lg font-bold text-stone-800 mb-2">잠시만 기다리세요</h3>
            <p className="text-stone-500 text-sm leading-relaxed">
              당신과 결이 맞는 이성을<br />찾고 있습니다...
            </p>
            <div className="mt-4 flex justify-center gap-1">
              {[0,1,2].map(i => (
                <div key={i} className="w-2 h-2 bg-burgundy-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI 매칭 결과 팝업 */}
      {aiMatches !== null && !aiMatching && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="ai-match-result">
          <div className="bg-white rounded-2xl p-6 max-w-md mx-4 w-full shadow-2xl max-h-[85vh] overflow-y-auto">
            <div className="text-center mb-5">
              <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              {aiMatches.length > 0 ? (
                <>
                  <h3 className="text-lg font-bold text-stone-800">AI가 추천하는 오늘의 짝</h3>
                  <p className="text-stone-400 text-sm mt-1">당신의 글에서 결이 맞는 이성을 찾았어요</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-stone-800">글이 등록되었습니다!</h3>
                  <p className="text-stone-400 text-sm mt-1">아직 매칭할 이성 회원이 부족해요. 곧 추천해드릴게요!</p>
                </>
              )}
            </div>

            {aiMatches.length > 0 && (
              <div className="space-y-3 mb-5">
                {aiMatches.map((match, idx) => {
                  const alreadySent = messageSentIds.includes(match.user_id);
                  const isActive = activeMessageUserId === match.user_id;
                  return (
                    <div
                      key={match.user_id || idx}
                      className="border border-stone-200 rounded-xl p-4 hover:border-burgundy-300 transition-all"
                      style={{ animation: `fadeSlideIn 0.5s ease ${idx * 0.15}s both` }}
                      data-testid={`ai-match-${idx}`}
                    >
                      <div className="flex items-start gap-3 cursor-pointer" onClick={() => navigate(`/community/author/${match.user_id}`)}>
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0 overflow-hidden ring-2 ring-transparent hover:ring-burgundy-300 transition-all ${
                          match.gender === "female" ? "bg-rose-100 text-rose-500" : "bg-blue-100 text-blue-500"
                        }`} data-testid={`ai-match-avatar-${idx}`}>
                          {match.profile_image ? (
                            <img src={match.profile_image} alt={match.username} className="w-full h-full object-cover" />
                          ) : (
                            match.gender === "female" ? "F" : "M"
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-stone-800 text-sm">{match.username}</span>
                            <MembershipBadge membership={match.membership} size="sm" />
                            <DocumentVerifiedBadge verified={match.document_verified} size="sm" />
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-stone-400">
                            {match.location && <span>{match.location}</span>}
                            {match.birth_year && <span>{match.birth_year}년생</span>}
                          </div>
                          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                            <p className="text-xs text-amber-700 font-medium">
                              <Sparkles className="w-3 h-3 inline mr-1" />
                              {match.reason}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* 메세지 보내기 버튼/폼 */}
                      <div className="mt-3 pl-15">
                        {alreadySent ? (
                          <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-medium" data-testid={`msg-sent-${idx}`}>
                            <Check className="w-3.5 h-3.5" />
                            메시지를 보냈습니다
                          </div>
                        ) : isActive ? (
                          <div className="space-y-2" data-testid={`msg-form-${idx}`}>
                            <textarea
                              value={messageText}
                              onChange={(e) => setMessageText(e.target.value)}
                              placeholder="첫 인사를 건네보세요..."
                              className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 resize-none"
                              rows={2}
                              maxLength={500}
                              autoFocus
                              data-testid={`msg-textarea-${idx}`}
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-stone-400">{messageText.length}/500</span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => { setActiveMessageUserId(null); setMessageText(""); }}
                                  className="px-3 py-1.5 text-xs text-stone-500 hover:text-stone-700 transition-colors"
                                  data-testid={`msg-cancel-${idx}`}
                                >
                                  취소
                                </button>
                                <button
                                  onClick={() => handleSendMessage(match.user_id)}
                                  disabled={messageSending || !messageText.trim()}
                                  className="px-4 py-1.5 text-xs font-medium text-white bg-burgundy-500 hover:bg-burgundy-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                                  data-testid={`msg-send-${idx}`}
                                >
                                  {messageSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                  보내기
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveMessageUserId(match.user_id); }}
                            className="flex items-center gap-1.5 text-xs font-medium text-burgundy-500 hover:text-burgundy-700 transition-colors py-1"
                            data-testid={`msg-btn-${idx}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                            메세지 보내기
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <style>{`@keyframes fadeSlideIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>

            <Button
              className="w-full btn-primary py-3"
              onClick={() => { setAiMatches(null); navigate("/community"); }}
              data-testid="ai-match-close-btn"
            >
              커뮤니티로 돌아가기
            </Button>
          </div>
        </div>
      )}
      <div className={embedded ? "max-w-none px-2 py-2" : "max-w-4xl mx-auto px-6 py-8"}>
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-stone-800">글쓰기</h1>
            {isAuthenticated && (
              <span className="text-sm text-stone-500">
                (작성자: <span className="font-medium text-burgundy-500">{user?.username}</span>)
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate("/community")}
              className="border-stone-300 text-stone-600"
            >
              취소
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedCategory || !title.trim() || !content.trim()}
              className="btn-primary flex items-center gap-2"
              data-testid="submit-post-btn"
            >
              {isSubmitting ? (
                <>처리중...</>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  등록
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
          {/* Category Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-stone-700 mb-2">
              카테고리 선택 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 text-left hover:border-burgundy-300 transition-colors"
                data-testid="category-selector"
              >
                {selectedCategoryInfo ? (
                  <span className="flex items-center gap-2">
                    <selectedCategoryInfo.icon className={`w-5 h-5 ${selectedCategoryInfo.color}`} />
                    <span className="text-stone-800">{selectedCategoryInfo.title}</span>
                  </span>
                ) : (
                  <span className="text-stone-400">카테고리를 선택하세요</span>
                )}
                <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform ${isCategoryOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isCategoryOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-200 max-h-64 overflow-y-auto z-10">
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <button
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setIsCategoryOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition-colors ${
                          category.isSub ? 'pl-10 py-2' : ''
                        } ${
                          selectedCategory === category.id ? 'bg-burgundy-50' : ''
                        }`}
                        data-testid={`category-option-${category.id}`}
                      >
                        {category.isSub ? (
                          <span className={`w-1.5 h-1.5 rounded-full ${category.color.replace('text-', 'bg-')}`} />
                        ) : (
                          <IconComponent className={`w-5 h-5 ${category.color}`} />
                        )}
                        <span className={category.isSub ? 'text-stone-500 text-sm' : 'text-stone-700 font-medium'}>{category.title}</span>
                        {selectedCategory === category.id && (
                          <Check className="w-4 h-4 text-burgundy-500 ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Title Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-stone-700 mb-2">
              제목 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목을 입력하세요"
              className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 transition-colors"
              maxLength={100}
              data-testid="title-input"
            />
            <p className="text-xs text-stone-400 mt-1 text-right">{title.length}/100</p>
          </div>

          {/* Content Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-stone-700 mb-2">
              내용 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="내용을 입력하세요. 서로를 존중하는 따뜻한 글을 부탁드려요."
              className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 transition-colors min-h-[300px] resize-none"
              maxLength={5000}
              data-testid="content-input"
            />
            <p className="text-xs text-stone-400 mt-1 text-right">{content.length}/5000</p>
          </div>

          {/* Media Upload Section */}
          <div className="border-t border-stone-200 pt-6">
            <label className="block text-sm font-medium text-stone-700 mb-4">
              미디어 첨부 (선택)
            </label>
            
            <div className="flex gap-3 mb-4">
              {/* Image Upload Button (label-wrapped for max browser compatibility) */}
              <label
                htmlFor="post-image-upload-input"
                className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg text-stone-600 transition-colors cursor-pointer select-none"
                data-testid="image-upload-btn"
              >
                <Image className="w-5 h-5" />
                <span className="text-sm">사진 추가</span>
                <span className="text-xs text-stone-400">({images.length}/10)</span>
              </label>
              <input
                id="post-image-upload-input"
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/*"
                multiple
                onChange={handleImageUpload}
                className="sr-only"
              />

              {/* Video Upload Button (label-wrapped for max browser compatibility) */}
              <label
                htmlFor="post-video-upload-input"
                className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg text-stone-600 transition-colors cursor-pointer select-none"
                data-testid="video-upload-btn"
              >
                <Video className="w-5 h-5" />
                <span className="text-sm">영상 추가</span>
                <span className="text-xs text-stone-400">({videos.length}/3)</span>
              </label>
              <input
                id="post-video-upload-input"
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/*"
                multiple
                onChange={handleVideoUpload}
                className="sr-only"
              />
            </div>

            {/* Image Previews */}
            {images.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-stone-500 mb-2">첨부된 사진</p>
                <div className="flex flex-wrap gap-3">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-24 h-24 object-cover rounded-lg border border-stone-200"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Video Previews */}
            {videos.length > 0 && (
              <div>
                <p className="text-xs text-stone-500 mb-2">첨부된 영상</p>
                <div className="flex flex-wrap gap-3">
                  {videos.map((video, index) => (
                    <div key={index} className="relative group">
                      <div className="w-40 h-24 bg-stone-900 rounded-lg flex items-center justify-center overflow-hidden">
                        <video
                          src={video.preview}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                        <Video className="w-8 h-8 text-white/80" />
                      </div>
                      <button
                        onClick={() => removeVideo(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <p className="text-xs text-stone-500 mt-1 truncate w-40">{video.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Guidelines */}
            <div className="mt-4 p-4 bg-stone-50 rounded-xl">
              <p className="text-xs text-stone-500">
                <span className="font-medium">업로드 안내</span><br />
                • 사진: 최대 10장, 각 10MB 이하 (JPG, PNG, GIF)<br />
                • 영상: 최대 3개, 각 50MB 이하 (MP4, MOV)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WritePostPage;
